import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, Check, XCircle, ArrowLeft } from 'lucide-react';
import { verifyEmail, ApiException } from '../api/client';
import { useAuth } from '../App';
import { Karte } from '../components/leitstand';

/**
 * Bestaetigung einer geaenderten E-Mail-Adresse.
 *
 * Wird ueber den Link aus der Bestaetigungsmail geoeffnet und laeuft ohne
 * Navigation — darum die mittige Karte im Leitstand-Stil ohne Rahmen.
 */
export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Dieser Link ist unvollständig. Bitte den Link aus der E-Mail noch einmal öffnen.');
      setLoading(false);
      return;
    }

    verifyEmail(token)
      .then(async () => {
        setSuccess(true);
        try {
          await refresh();
        } catch {}
      })
      .catch((err) => {
        if (err instanceof ApiException) {
          setError(
            err.data.error ||
              'Die Bestätigung hat nicht geklappt. Bitte den Link aus der E-Mail noch einmal öffnen.',
          );
        } else {
          setError('Keine Verbindung zum Server. Bitte noch einmal versuchen.');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <p className="mb-5 text-center font-zahl text-[15px] font-semibold tracking-[0.06em] text-tinte-2">
          wash<b className="font-semibold text-akzent">·</b>ops
        </p>

        <Karte titel="E-Mail-Adresse bestätigen">
          <div className="text-center">
            {loading ? (
              <>
                <Loader2 className="w-12 h-12 text-tinte-3 animate-spin mx-auto mb-4" />
                <p className="text-tinte-2">E-Mail wird bestätigt...</p>
              </>
            ) : success ? (
              <>
                <div className="w-16 h-16 rounded-full bg-flaeche-2 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-lauf" />
                </div>
                <p className="text-tinte font-medium mb-2">E-Mail bestätigt</p>
                <p className="text-tinte-2 mb-6">Ihre E-Mail-Adresse wurde geändert.</p>
                <Link
                  to="/profile"
                  className="inline-flex items-center justify-center gap-2 w-full rounded-md border border-akzent bg-akzent px-4 py-2 font-medium text-akzent-satz transition-colors"
                >
                  Zum Profil
                </Link>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-flaeche-2 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-stoer" />
                </div>
                <p className="text-tinte font-medium mb-2">Nicht bestätigt</p>
                <p className="text-tinte-2 mb-6">{error}</p>
                <Link
                  to="/profile"
                  className="inline-flex items-center gap-2 text-tinte-2 transition-colors hover:text-tinte"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Zum Profil
                </Link>
              </>
            )}
          </div>
        </Karte>
      </div>
    </div>
  );
}
